import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AppShell } from '@/shared/ui/app-shell'
import { PageHeader, PageHeaderSpacer } from '@/shared/ui/page-header'
import { useTelegram, useBackButton } from '@/shared/lib/telegram'
import { requireAuth } from '@/shared/lib/router'
import { useOrders } from '@/features/orders/api/use-orders'
import { OrderCard } from '@/features/orders/ui/order-card'
import { ProductListSkeleton, Button } from '@/shared/ui'
import { Package, ShoppingBag } from 'lucide-react'

export const Route = createFileRoute('/orders')({
  component: OrdersPage,
})

// Склонение слова "заказ"
function getOrderCountText(count: number): string | undefined {
  if (count === 0) return undefined
  if (count === 1) return '1 заказ'
  if (count >= 2 && count <= 4) return `${count} заказа`
  return `${count} заказов`
}

function OrdersPage() {
  const { initData, hapticFeedback } = useTelegram()
  const navigate = useNavigate()

  // Нативная кнопка "Назад" → профиль
  useBackButton(true, () => navigate({ to: '/profile' }))

  // Проверяем авторизацию
  requireAuth(initData)

  const { data: orders, isLoading, error } = useOrders()

  return (
    <AppShell showBottomNav>
      <PageHeader 
        title="Мои заказы" 
        subtitle={isLoading ? undefined : getOrderCountText(orders?.length || 0)}
        hideBackButton
      />

      <PageHeaderSpacer />

      <main className="content-container pb-6">
        {/* Загрузка */}
        {isLoading && (
          <div className="space-y-3 pt-4">
            <ProductListSkeleton count={3} />
          </div>
        )}

        {/* Ошибка */}
        {error && (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full mb-4">
              <Package className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-gray-500 mb-4">Не удалось загрузить заказы</p>
            <Button 
              variant="secondary" 
              onClick={() => window.location.reload()}
            >
              Попробовать снова
            </Button>
          </div>
        )}

        {/* Пустой список */}
        {!isLoading && !error && orders?.length === 0 && (
          <div className="text-center py-16 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 dark:bg-dark-elevated rounded-full mb-6">
              <ShoppingBag className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-ink dark:text-white mb-2">
              Заказов пока нет
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-xs mx-auto">
              Ваши заказы появятся здесь после оформления
            </p>
            <Button 
              onClick={() => {
                hapticFeedback?.impactOccurred('light')
                navigate({ to: '/catalog/$section', params: { section: 'tea' } })
              }}
            >
              Перейти в каталог
            </Button>
          </div>
        )}

        {/* Список заказов */}
        {!isLoading && !error && orders && orders.length > 0 && (
          <div className="space-y-3 pt-4 animate-in fade-in duration-300">
            {orders.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>
        )}
      </main>
    </AppShell>
  )
}
